import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Image,
  IconButton,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  Spinner,
  Center
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, ViewIcon } from '@chakra-ui/icons';
import StorageImage from './StorageImage';

interface CategoryPhotoCaptureProps {
  label: string;
  isRequired?: boolean;
  existingPhotoUrl?: string;
  currentPhotoFile?: File;
  onPhotoCapture: (file: File) => void;
  onPhotoRemove: () => void;
  isUploading?: boolean;
}

const CategoryPhotoCapture: React.FC<CategoryPhotoCaptureProps> = ({
  label,
  isRequired = false,
  existingPhotoUrl,
  currentPhotoFile,
  onPhotoCapture,
  onPhotoRemove,
  isUploading = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const toast = useToast();

  // Update preview URL when currentPhotoFile changes
  useEffect(() => {
    if (currentPhotoFile) {
      const url = URL.createObjectURL(currentPhotoFile);
      setPreviewUrl(url);
      
      // Cleanup previous URL
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setPreviewUrl('');
    }
  }, [currentPhotoFile]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please select an image file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please select an image smaller than 10MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Call the parent callback (this will trigger useEffect above)
    onPhotoCapture(file);
  };

  const handleRemovePhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onPhotoRemove();
  };

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Priority: currentPhotoFile > previewUrl > existingPhotoUrl
  const displayUrl = previewUrl || existingPhotoUrl;
  const hasPhoto = !!currentPhotoFile || !!existingPhotoUrl;
  
  // Helper function to check if URL is a storage path
  const isStoragePath = (url: string) => {
    return url && !url.startsWith('http') && !url.startsWith('blob:');
  };

  // Render the appropriate image component
  const renderImage = () => {
    if (!displayUrl) return null;
    
    if (previewUrl) {
      // For preview URLs (blob URLs), use regular Image component
      return (
        <Image
          src={displayUrl}
          alt={label}
          w="100%"
          maxH="300px"
          objectFit="contain"
          bg="gray.100"
        />
      );
    } else if (existingPhotoUrl && isStoragePath(existingPhotoUrl)) {
      // For storage paths, use StorageImage component
      return (
        <StorageImage
          storagePath={existingPhotoUrl}
          alt={label}
          w="100%"
          maxH="300px"
          objectFit="contain"
        />
      );
    } else {
      // For regular URLs, use Image component
      return (
        <Image
          src={displayUrl}
          alt={label}
          w="100%"
          maxH="300px"
          objectFit="contain"
          bg="gray.100"
        />
      );
    }
  };

  return (
    <FormControl isRequired={isRequired}>
      <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
        {label} {isRequired && <Text as="span" color="red.500">*</Text>}
      </FormLabel>
      
      <VStack spacing={4} align="stretch">
        {/* Photo Preview */}
        {hasPhoto && (
          <Box
            border="2px solid"
            borderColor="gray.200"
            borderRadius="md"
            overflow="hidden"
            position="relative"
            bg="gray.50"
            maxH="300px"
            minH="200px"
          >
            {isUploading ? (
              <Center h="200px">
                <VStack spacing={2}>
                  <Spinner size="lg" color="blue.500" />
                  <Text fontSize="sm" color="gray.600">Uploading...</Text>
                </VStack>
              </Center>
            ) : (
              <>
                {renderImage()}
                <Box
                  position="absolute"
                  top={2}
                  right={2}
                  bg="rgba(0,0,0,0.6)"
                  borderRadius="md"
                  p={1}
                >
                  <IconButton
                    aria-label="Remove photo"
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                    onClick={handleRemovePhoto}
                  />
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Capture Controls */}
        {!hasPhoto && (
          <Box
            border="2px dashed"
            borderColor="gray.300"
            borderRadius="md"
            p={6}
            textAlign="center"
            bg="gray.50"
            _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
            transition="all 0.2s"
          >
            <VStack spacing={3}>
              <AddIcon color="gray.400" boxSize={8} />
              <VStack spacing={1}>
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  {isRequired ? 'Photo Required' : 'Add Photo (Optional)'}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Tap to take a photo or select from gallery
                </Text>
              </VStack>
            </VStack>
          </Box>
        )}

        {/* Action Buttons */}
        <HStack spacing={3} justify="center">
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            variant={hasPhoto ? "outline" : "solid"}
            size="sm"
            onClick={handleCameraCapture}
            isDisabled={isUploading}
          >
            {hasPhoto ? 'Change Photo' : 'Take Photo'}
          </Button>
          
          {hasPhoto && (
            <Button
              leftIcon={<ViewIcon />}
              variant="ghost"
              size="sm"
              onClick={() => {
                if (displayUrl) {
                  window.open(displayUrl, '_blank');
                }
              }}
              isDisabled={isUploading}
            >
              View Full
            </Button>
          )}
        </HStack>

        {/* Required Photo Warning */}
        {isRequired && !hasPhoto && (
          <Alert status="warning" size="sm" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              A photo is required for this step before you can proceed.
            </Text>
          </Alert>
        )}

        {/* Hidden File Input */}
        <Input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </VStack>
    </FormControl>
  );
};

export default CategoryPhotoCapture;